import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { StripedWordmark, TricolorStrip } from '../components/BSCAuthLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  LogOut, Plus, Trash2, Upload, FileText, Package, Store as StoreIcon, ExternalLink, QrCode, Printer,
  LayoutDashboard, Boxes, Users as UsersIcon, Wrench, ClipboardCheck, Truck, Settings as SettingsIcon,
  ShieldCheck, AlertTriangle, ChevronRight, ArrowLeft, Building2, UserCheck, Download, Search,
  BarChart3, FileUp, Send, Sparkles, Calculator, Files, Camera,
} from 'lucide-react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  working:        { bg: '#E8F5E9', color: '#346639', label: 'Working' },
  active:         { bg: '#E8F5E9', color: '#346639', label: 'Working' },
  'under-service':{ bg: '#FFF8E1', color: '#C77D38', label: 'Under Service' },
  'in-repair':    { bg: '#FFF8E1', color: '#C77D38', label: 'Under Service' },
  damaged:        { bg: '#FEE',    color: '#A43E33', label: 'Damaged' },
  scrapped:       { bg: '#F5F5F5', color: '#5C5C5C', label: 'Scrapped' },
  disposed:       { bg: '#F5F5F5', color: '#5C5C5C', label: 'Scrapped' },
  replaced:       { bg: '#E3F2FD', color: '#1565C0', label: 'Replaced' },
};

const PAYMENT_MODES = ['UPI', 'Card', 'Bank Transfer', 'Auto-debit', 'Cash', 'Cheque'];
const STATUSES = ['working', 'under-service', 'damaged', 'scrapped', 'replaced'];
const DEPR_METHODS = ['none', 'slm', 'wdv'];
const MAINT_TYPES = ['preventive', 'breakdown', 'amc'];
const MAINT_STATUSES = ['scheduled', 'in-progress', 'completed', 'cancelled'];
const CONS_UNITS = ['PC', 'KG', 'GM', 'LTR', 'ML', 'MTR', 'BOX', 'SET', 'ROLL'];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const NAV = [
  { key: 'dashboard',   label: 'Dashboard',   icon: LayoutDashboard },
  { key: 'assets',      label: 'Assets',      icon: Package },
  { key: 'inventory',   label: 'Inventory',   icon: Boxes },
  { key: 'assignments', label: 'Assignments', icon: UserCheck },
  { key: 'transfers',   label: 'Transfers',   icon: Send },
  { key: 'maintenance', label: 'Maintenance', icon: Wrench },
  { key: 'audits',      label: 'Audits',      icon: ClipboardCheck },
  { key: 'approvals',   label: 'Approvals',   icon: ShieldCheck, adminOnly: true },
  { key: 'reports',     label: 'Reports',     icon: BarChart3, adminOnly: true },
  { key: 'vendors',     label: 'Vendors',     icon: Truck },
  { key: 'employees',   label: 'Employees',   icon: UsersIcon },
  { key: 'settings',    label: 'Settings',    icon: SettingsIcon, adminOnly: true },
];

// ============================================================
// Main shell
// ============================================================
export default function Assets() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [section, setSection] = useState('dashboard');

  // Shared masters
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  const loadMasters = useCallback(async () => {
    try {
      const [v, c, s, e, b, d] = await Promise.all([
        axios.get(`${API}/assets/vendors`,     { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/assets/categories`,  { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/assets/stores`,      { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/assets/employees`,   { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/assets/branches`,    { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/assets/departments`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setVendors(v.data || []); setCategories(c.data || []); setStores(s.data || []);
      setEmployees(e.data || []); setBranches(b.data || []); setDepartments(d.data || []);
    } catch (err) { console.error(err); }
  }, []);
  useEffect(() => { loadMasters(); }, [loadMasters]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const masters = { vendors, categories, stores, employees, branches, departments, reload: loadMasters };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F7F5F0' }}>
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#0A0A0A', borderColor: '#1a1a1a' }}>
        <div className="px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-to-launcher">
            <img src="/bsc-logo.jpeg" alt="BSC" className="w-9 h-9 rounded-lg" />
            <div className="flex flex-col leading-tight">
              <StripedWordmark text="ASSETS" size="sm" />
              <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">Asset Tracker · ERP</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-white/70 text-sm">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white" data-testid="logout-button">
              <LogOut size={14} className="mr-1" />Logout
            </Button>
          </div>
        </div>
        <TricolorStrip />
      </div>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-56 border-r flex-shrink-0" style={{ backgroundColor: '#FFF', borderColor: '#E8E5DF', minHeight: 'calc(100vh - 60px)' }}>
          <nav className="p-3 space-y-1">
            {NAV.filter(n => !n.adminOnly || isAdmin).map(n => {
              const Icon = n.icon;
              const active = section === n.key;
              return (
                <button key={n.key} onClick={() => setSection(n.key)} data-testid={`nav-${n.key}`}
                  className="w-full text-left text-sm flex items-center gap-2 px-3 py-2 rounded-md transition-colors"
                  style={active
                    ? { backgroundColor: '#1E3B29', color: '#FFF' }
                    : { color: '#1A1A1A' }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#F7F5F0'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  <Icon size={14} />
                  <span>{n.label}</span>
                  {active && <ChevronRight size={12} className="ml-auto" />}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6 overflow-x-hidden">
          {section === 'dashboard'   && <DashboardSection isAdmin={isAdmin} masters={masters} onJump={setSection} />}
          {section === 'assets'      && <AssetsSection      isAdmin={isAdmin} masters={masters} />}
          {section === 'inventory'   && <InventorySection   isAdmin={isAdmin} masters={masters} />}
          {section === 'assignments' && <AssignmentsSection isAdmin={isAdmin} masters={masters} />}
          {section === 'transfers'   && <TransfersSection   isAdmin={isAdmin} />}
          {section === 'maintenance' && <MaintenanceSection isAdmin={isAdmin} masters={masters} />}
          {section === 'audits'      && <AuditsSection      isAdmin={isAdmin} masters={masters} />}
          {section === 'approvals'   && isAdmin && <ApprovalsSection />}
          {section === 'reports'     && isAdmin && <ReportsSection masters={masters} />}
          {section === 'vendors'     && <VendorsSection     isAdmin={isAdmin} masters={masters} />}
          {section === 'employees'   && <EmployeesSection   employees={employees} />}
          {section === 'settings'    && isAdmin && <SettingsSection masters={masters} />}
        </main>
      </div>
    </div>
  );
}

// ============================================================
// Reusable bits
// ============================================================
const SectionHeader = ({ title, subtitle, children }) => (
  <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
    <div>
      <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope', color: '#1A1A1A' }}>{title}</h1>
      {subtitle && <p className="text-xs mt-0.5" style={{ color: '#5C5C5C' }}>{subtitle}</p>}
    </div>
    <div className="flex items-center gap-2 flex-wrap">{children}</div>
  </div>
);

const StatCard = ({ label, value, color, icon: Icon, sub }) => (
  <div className="p-4 rounded-lg border" style={{ backgroundColor: '#FFF', borderColor: '#E8E5DF' }}>
    <div className="flex items-center justify-between mb-1">
      <div className="text-[10px] uppercase tracking-wider" style={{ color: '#5C5C5C' }}>{label}</div>
      {Icon && <Icon size={14} style={{ color }} />}
    </div>
    <div className="text-2xl font-semibold" style={{ fontFamily: 'Manrope', color: color || '#1A1A1A' }}>{value ?? '—'}</div>
    {sub && <div className="text-[10px] mt-1" style={{ color: '#5C5C5C' }}>{sub}</div>}
  </div>
);

const StatusPill = ({ status }) => {
  const s = STATUS_BADGE[status] || { bg: '#F5F5F5', color: '#5C5C5C', label: status || '—' };
  return <span className="px-2 py-0.5 rounded text-[10px] uppercase font-semibold" style={{ backgroundColor: s.bg, color: s.color }}>{s.label}</span>;
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg border p-5 ${className}`} style={{ borderColor: '#E8E5DF' }}>{children}</div>
);

// ============================================================
// 1. Dashboard
// ============================================================
function DashboardSection({ isAdmin, masters, onJump }) {
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/assets/dashboard/summary`, { withCredentials: true });
      setD(r.data || null);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <p className="text-sm" style={{ color: '#5C5C5C' }}>Loading dashboard…</p>;
  const t = d.totals || {};
  const a = d.alerts || {};
  return (
    <>
      <SectionHeader title="Asset Dashboard" subtitle="Real-time overview of company assets, alerts & approvals">
        <Button variant="outline" size="sm" onClick={load} data-testid="dash-refresh">Refresh</Button>
      </SectionHeader>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Total Assets" value={t.total} color="#1A1A1A" icon={Package} />
        <StatCard label="Working" value={t.working} color="#346639" icon={ShieldCheck} />
        <StatCard label="Under Service" value={t.under_service} color="#C77D38" icon={Wrench} />
        <StatCard label="Damaged" value={t.damaged} color="#A43E33" icon={AlertTriangle} />
        <StatCard label="Scrapped" value={t.scrapped} color="#5C5C5C" />
        <StatCard label="Replaced" value={t.replaced} color="#1565C0" />
        <StatCard label="Currently Assigned" value={t.assigned} color="#1E3B29" icon={UserCheck} />
        <StatCard label="Total Asset Value" value={`₹${(t.value || 0).toLocaleString('en-IN')}`} color="#1E3B29" />
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <Card>
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium flex items-center gap-2"><AlertTriangle size={16} style={{ color: '#A43E33' }} />Warranty Expiring (≤30 days)</h3>
            <span className="text-xs" style={{ color: '#5C5C5C' }}>{a.warranty_expiring?.length || 0}</span>
          </div>
          {a.warranty_expiring?.length ? (
            <ul className="text-sm space-y-1 max-h-44 overflow-y-auto">
              {a.warranty_expiring.map(w => (
                <li key={w.asset_code} className="flex justify-between border-b py-1" style={{ borderColor: '#E8E5DF' }}>
                  <span><span className="font-mono text-xs mr-2">{w.asset_code}</span>{w.name}</span>
                  <span className="text-xs" style={{ color: '#A43E33' }}>{w.warranty_end}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-xs" style={{ color: '#5C5C5C' }}>No warranties expiring soon. 🎉</p>}
        </Card>

        <Card>
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium flex items-center gap-2"><Wrench size={16} style={{ color: '#C77D38' }} />AMC Renewals (≤30 days)</h3>
            <span className="text-xs" style={{ color: '#5C5C5C' }}>{a.amc_expiring?.length || 0}</span>
          </div>
          {a.amc_expiring?.length ? (
            <ul className="text-sm space-y-1 max-h-44 overflow-y-auto">
              {a.amc_expiring.map(w => (
                <li key={w.asset_code} className="flex justify-between border-b py-1" style={{ borderColor: '#E8E5DF' }}>
                  <span><span className="font-mono text-xs mr-2">{w.asset_code}</span>{w.name}</span>
                  <span className="text-xs" style={{ color: '#C77D38' }}>{w.amc_end}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-xs" style={{ color: '#5C5C5C' }}>All AMCs up to date.</p>}
        </Card>

        <Card>
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium flex items-center gap-2"><Boxes size={16} style={{ color: '#A43E33' }} />Low Stock Items</h3>
            <span className="text-xs" style={{ color: '#5C5C5C' }}>{a.low_stock?.length || 0}</span>
          </div>
          {a.low_stock?.length ? (
            <ul className="text-sm space-y-1 max-h-44 overflow-y-auto">
              {a.low_stock.map(c => (
                <li key={c.id} className="flex justify-between border-b py-1" style={{ borderColor: '#E8E5DF' }}>
                  <span>{c.name}</span>
                  <span className="text-xs" style={{ color: '#A43E33' }}>{c.stock}/{c.reorder_level} {c.unit}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-xs" style={{ color: '#5C5C5C' }}>Inventory levels healthy.</p>}
        </Card>

        <Card>
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium flex items-center gap-2"><AlertTriangle size={16} style={{ color: '#A43E33' }} />Overdue Returns</h3>
            <span className="text-xs" style={{ color: '#5C5C5C' }}>{a.overdue_returns?.length || 0}</span>
          </div>
          {a.overdue_returns?.length ? (
            <ul className="text-sm space-y-1 max-h-44 overflow-y-auto">
              {a.overdue_returns.map((r, i) => (
                <li key={i} className="flex justify-between border-b py-1" style={{ borderColor: '#E8E5DF' }}>
                  <span className="font-mono text-xs">{r.asset_code}</span>
                  <span className="text-xs" style={{ color: '#A43E33' }}>was due {r.expected_return}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-xs" style={{ color: '#5C5C5C' }}>No overdue returns.</p>}
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <h3 className="font-medium mb-2 text-sm">Pending Approvals</h3>
          <div className="text-3xl font-semibold" style={{ color: a.pending_approvals > 0 ? '#A43E33' : '#346639' }}>{a.pending_approvals ?? 0}</div>
          {isAdmin && <Button size="sm" variant="outline" className="mt-2" onClick={() => onJump('approvals')} data-testid="dash-jump-approvals">Review →</Button>}
        </Card>
        <Card>
          <h3 className="font-medium mb-2 text-sm">Open Audits</h3>
          <div className="text-3xl font-semibold" style={{ color: '#1E3B29' }}>{a.open_audits ?? 0}</div>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => onJump('audits')} data-testid="dash-jump-audits">View →</Button>
        </Card>
        <Card>
          <h3 className="font-medium mb-2 text-sm">Branch Distribution</h3>
          <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
            {Object.entries(d.by_branch || {}).map(([b, c]) => (
              <li key={b} className="flex justify-between"><span>{b}</span><span style={{ color: '#5C5C5C' }}>{c}</span></li>
            ))}
          </ul>
        </Card>
      </div>
    </>
  );
}

// ============================================================
// 2. Assets — list / create / edit / detail / QR / assign / return
// ============================================================
function AssetsSection({ isAdmin, masters }) {
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [billFile, setBillFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [detail, setDetail] = useState(null);
  const [qrAsset, setQrAsset] = useState(null);
  const [assignOpen, setAssignOpen] = useState(null);
  const [assignForm, setAssignForm] = useState({});
  const [returnOpen, setReturnOpen] = useState(null);
  const [returnForm, setReturnForm] = useState({ condition: 'good', notes: '', damage_cost: 0 });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/assets`, { withCredentials: true });
      setAssets(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter(a => {
      if (statusFilter !== 'all') {
        const st = (a.status === 'active' ? 'working' : a.status === 'in-repair' ? 'under-service' : a.status === 'disposed' ? 'scrapped' : a.status);
        if (st !== statusFilter) return false;
      }
      if (!q) return true;
      return [a.asset_code, a.name, a.brand, a.model, a.serial_no, a.category, a.branch]
        .some(v => (v || '').toString().toLowerCase().includes(q));
    });
  }, [assets, search, statusFilter]);

  const openCreate = () => { setEditing(null); setForm({ name: '', category: '', value: 0, status: 'working' }); setBillFile(null); setImageFiles([]); setCreateOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a }); setBillFile(null); setImageFiles([]); setCreateOpen(true); };

  const onBill = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setBillFile({ filename: f.name, data: await fileToBase64(f) });
  };
  const onImages = async (e) => {
    const fs = Array.from(e.target.files || []); const out = [];
    for (const f of fs) {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} >5MB`); continue; }
      out.push({ filename: f.name, data: await fileToBase64(f) });
    }
    setImageFiles((p) => [...p, ...out]);
  };

  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    const payload = { ...form };
    if (billFile) { payload.bill_copy = billFile.data; payload.bill_filename = billFile.filename; }
    if (imageFiles.length) payload.images = [...(form.images || []), ...imageFiles];
    ['vendor_id', 'assigned_staff_id', 'store_id'].forEach(k => { if (!payload[k]) payload[k] = null; });
    try {
      if (editing) await axios.put(`${API}/assets/${editing.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/assets`, payload, { withCredentials: true });
      toast.success(editing ? 'Updated' : 'Created'); setCreateOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete ${a.asset_code}?`)) return;
    try { await axios.delete(`${API}/assets/${a.id}`, { withCredentials: true }); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openAssign = (a) => { setAssignOpen(a); setAssignForm({ staff_id: '', site: '', project: '', expected_return: '', notes: '' }); };
  const submitAssign = async () => {
    if (!assignForm.staff_id && !assignForm.site) { toast.error('Pick a staff member or enter a site'); return; }
    try {
      const payload = { asset_id: assignOpen.id, ...assignForm };
      if (!payload.staff_id) payload.staff_id = null;
      await axios.post(`${API}/assets/${assignOpen.id}/assign`, payload, { withCredentials: true });
      toast.success('Asset assigned'); setAssignOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openReturn = (a) => { setReturnOpen(a); setReturnForm({ condition: 'good', notes: '', damage_cost: 0 }); };
  const submitReturn = async () => {
    try {
      await axios.post(`${API}/assets/${returnOpen.id}/return`, returnForm, { withCredentials: true });
      toast.success('Asset returned'); setReturnOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const exportXls = () => { window.location.href = `${API}/assets/export/excel`; };

  // ---- Bulk import (h) ----
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    return lines.slice(1).map(line => {
      const cells = line.split(',').map(c => c.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cells[i] || ''; });
      return obj;
    });
  };
  const onCsvFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const text = await f.text();
    const rows = parseCsv(text);
    setBulkRows(rows);
    toast.success(`Parsed ${rows.length} row(s) — review then click Import`);
  };
  const submitBulk = async () => {
    if (bulkRows.length === 0) { toast.error('No rows parsed'); return; }
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/assets/bulk-import`, { rows: bulkRows }, { withCredentials: true });
      toast.success(`Imported ${r.data.created_count} asset(s)${r.data.errors?.length ? `, ${r.data.errors.length} errors` : ''}`);
      setBulkOpen(false); setBulkRows([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBulkBusy(false); }
  };

  // ---- Transfer (i) ----
  const [xferOpen, setXferOpen] = useState(null);
  const [xferForm, setXferForm] = useState({});
  const openTransfer = (a) => { setXferOpen(a); setXferForm({}); };
  const submitTransfer = async () => {
    if (!xferForm.to_branch && !xferForm.to_store_id && !xferForm.to_staff_id) {
      toast.error('Pick a destination (branch, store, or staff)'); return;
    }
    try {
      await axios.post(`${API}/assets/transfer`, { asset_id: xferOpen.id, ...xferForm }, { withCredentials: true });
      toast.success('Transfer initiated — awaiting receiver acknowledgment');
      setXferOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ---- Document vault (j) ----
  const [docOpen, setDocOpen] = useState(null);
  const [docList, setDocList] = useState([]);
  const [docForm, setDocForm] = useState({ kind: 'invoice', filename: '', data: '', note: '' });
  const openDocs = async (a) => {
    setDocOpen(a); setDocForm({ kind: 'invoice', filename: '', data: '', note: '' });
    // Documents are stored inline on the asset doc
    setDocList(a.documents || []);
  };
  const onDocFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setDocForm({ ...docForm, filename: f.name, data: await fileToBase64(f) });
  };
  const submitDoc = async () => {
    if (!docForm.data || !docForm.filename) { toast.error('Pick a file first'); return; }
    try {
      const r = await axios.post(`${API}/assets/${docOpen.id}/documents`, docForm, { withCredentials: true });
      setDocList([...docList, r.data]);
      setDocForm({ kind: 'invoice', filename: '', data: '', note: '' });
      toast.success('Document added');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const delDoc = async (doc_id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/assets/${docOpen.id}/documents/${doc_id}`, { withCredentials: true });
      setDocList(docList.filter(d => d.id !== doc_id));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ---- Depreciation (b) ----
  const [deprOpen, setDeprOpen] = useState(null);
  const [deprData, setDeprData] = useState(null);
  const openDepr = async (a) => {
    setDeprOpen(a); setDeprData(null);
    try {
      const r = await axios.get(`${API}/assets/${a.id}/depreciation`, { withCredentials: true });
      setDeprData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ---- AI predict maintenance (e) ----
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const aiPredict = async (a) => {
    setAiBusy(true); setAiResult(null);
    try {
      const r = await axios.post(`${API}/assets/${a.id}/ai-predict-maintenance`, {}, { withCredentials: true });
      setAiResult({ asset: a, ...r.data });
    } catch (e) { toast.error(e.response?.data?.detail || 'AI prediction failed'); }
    finally { setAiBusy(false); }
  };

  return (
    <>
      <SectionHeader title="Assets" subtitle={`${filtered.length} of ${assets.length} assets`}>
        <Input placeholder="Search code / name / brand…" value={search} onChange={(e) => setSearch(e.target.value)}
               className="w-60" data-testid="asset-search" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger style={{ width: 140 }} data-testid="asset-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_BADGE[s]?.label || s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={exportXls} data-testid="export-excel"><Download size={14} className="mr-1" />Excel</Button>
        {isAdmin && <Button variant="outline" size="sm" onClick={() => { setBulkOpen(true); setBulkRows([]); }} data-testid="bulk-import-btn"><FileUp size={14} className="mr-1" />Bulk Import</Button>}
        {isAdmin && <Button onClick={openCreate} data-testid="add-asset-btn" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}><Plus size={14} className="mr-1" />New Asset</Button>}
      </SectionHeader>

      <Card>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F7F5F0' }}>
            <tr>
              <th className="text-left p-2">Code</th><th className="text-left p-2">Name</th>
              <th className="text-left p-2">Category</th><th className="text-left p-2">Brand/Model</th>
              <th className="text-left p-2">Branch</th><th className="text-left p-2">Assigned</th>
              <th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(a => {
              const emp = masters.employees.find(e => e.id === a.assigned_staff_id);
              return (
                <tr key={a.id} className="border-t hover:bg-stone-50" style={{ borderColor: '#E8E5DF' }} data-testid={`asset-row-${a.asset_code}`}>
                  <td className="p-2 font-mono text-xs">{a.asset_code}</td>
                  <td className="p-2 font-medium">{a.name}</td>
                  <td className="p-2 text-xs">{a.category || '—'}</td>
                  <td className="p-2 text-xs">{a.brand || ''} {a.model ? `· ${a.model}` : ''}</td>
                  <td className="p-2 text-xs">{a.branch || '—'}</td>
                  <td className="p-2 text-xs">{emp ? emp.name : (a.assigned_site || '—')}</td>
                  <td className="p-2"><StatusPill status={a.status} /></td>
                  <td className="p-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDetail(a)} data-testid={`view-${a.asset_code}`}><FileText size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setQrAsset(a)} data-testid={`qr-${a.asset_code}`}><QrCode size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => openDocs(a)} data-testid={`docs-${a.asset_code}`} title="Document vault"><Files size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => openDepr(a)} data-testid={`depr-${a.asset_code}`} title="Depreciation"><Calculator size={12} /></Button>
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => aiPredict(a)} disabled={aiBusy} data-testid={`ai-${a.asset_code}`} style={{ color: '#1565C0' }} title="AI predict maintenance"><Sparkles size={12} /></Button>}
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => openTransfer(a)} data-testid={`xfer-${a.asset_code}`} style={{ color: '#1565C0' }} title="Transfer"><Send size={12} /></Button>}
                      {isAdmin && !a.assigned_staff_id && !a.assigned_site && <Button size="sm" variant="ghost" onClick={() => openAssign(a)} data-testid={`assign-${a.asset_code}`} style={{ color: '#1E3B29' }}><UserCheck size={12} /></Button>}
                      {isAdmin && (a.assigned_staff_id || a.assigned_site) && <Button size="sm" variant="ghost" onClick={() => openReturn(a)} data-testid={`return-${a.asset_code}`} style={{ color: '#C77D38' }}><ArrowLeft size={12} /></Button>}
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-${a.asset_code}`}><SettingsIcon size={12} /></Button>}
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => del(a)} style={{ color: '#A43E33' }} data-testid={`del-${a.asset_code}`}><Trash2 size={12} /></Button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-10 text-center text-sm" style={{ color: '#5C5C5C' }}>No assets match your filters.</div>}
      </Card>

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="asset-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.asset_code}` : 'New Asset'}</DialogTitle>
            <DialogDescription>Code is auto-generated. Upload bill copy + photos for full traceability.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="asset-name" /></div>
            <div className="col-span-2"><Label>Description</Label><textarea className="w-full border rounded p-2 text-sm" rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

            <div><Label>Category</Label>
              <Select value={form.category || 'none'} onValueChange={(v) => setForm({ ...form, category: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="asset-category"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{masters.categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status || 'working'} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="asset-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_BADGE[s]?.label || s}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div><Label>Brand</Label><Input value={form.brand || ''} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="asset-brand" /></div>
            <div><Label>Model #</Label><Input value={form.model || ''} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            <div><Label>Serial #</Label><Input value={form.serial_no || ''} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} /></div>
            <div><Label>Value (₹)</Label><Input type="number" value={form.value || 0} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} /></div>

            <div><Label>Vendor</Label>
              <Select value={form.vendor_id || 'none'} onValueChange={(v) => setForm({ ...form, vendor_id: v === 'none' ? null : v })}>
                <SelectTrigger data-testid="asset-vendor"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{masters.vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Branch</Label>
              <Select value={form.branch || 'none'} onValueChange={(v) => setForm({ ...form, branch: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{masters.branches.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Department</Label>
              <Select value={form.department || 'none'} onValueChange={(v) => setForm({ ...form, department: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{masters.departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Store / Location</Label>
              <Select value={form.store_id || 'none'} onValueChange={(v) => setForm({ ...form, store_id: v === 'none' ? null : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{masters.stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div><Label>Date of Purchase</Label><Input type="date" value={form.date_of_purchase || ''} onChange={(e) => setForm({ ...form, date_of_purchase: e.target.value })} /></div>
            <div><Label>Payment Mode</Label>
              <Select value={form.payment_mode || 'none'} onValueChange={(v) => setForm({ ...form, payment_mode: v === 'none' ? null : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{PAYMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div><Label>Warranty Start</Label><Input type="date" value={form.warranty_start || ''} onChange={(e) => setForm({ ...form, warranty_start: e.target.value })} /></div>
            <div><Label>Warranty End</Label><Input type="date" value={form.warranty_end || ''} onChange={(e) => setForm({ ...form, warranty_end: e.target.value })} data-testid="warranty-end" /></div>
            <div><Label>AMC Start</Label><Input type="date" value={form.amc_start || ''} onChange={(e) => setForm({ ...form, amc_start: e.target.value })} /></div>
            <div><Label>AMC End</Label><Input type="date" value={form.amc_end || ''} onChange={(e) => setForm({ ...form, amc_end: e.target.value })} data-testid="amc-end" /></div>

            <div><Label>Expected Life (yrs)</Label><Input type="number" step="0.5" value={form.expected_life_years || ''} onChange={(e) => setForm({ ...form, expected_life_years: parseFloat(e.target.value) || null })} /></div>
            <div><Label>Depreciation</Label>
              <Select value={form.depreciation_method || 'none'} onValueChange={(v) => setForm({ ...form, depreciation_method: v })}>
                <SelectTrigger data-testid="depr-method"><SelectValue /></SelectTrigger>
                <SelectContent>{DEPR_METHODS.map(m => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Residual Value (₹)</Label><Input type="number" value={form.residual_value || ''} onChange={(e) => setForm({ ...form, residual_value: parseFloat(e.target.value) || null })} /></div>
            <div><Label>Cost Type</Label>
              <Select value={form.cost_type || 'one-time'} onValueChange={(v) => setForm({ ...form, cost_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="one-time">One-time</SelectItem><SelectItem value="recurring">Recurring</SelectItem></SelectContent>
              </Select>
            </div>

            <div className="col-span-2"><Label>Bill / Invoice copy</Label>
              <input type="file" onChange={onBill} accept=".pdf,image/*" className="text-sm" data-testid="bill-upload" />
              {billFile && <div className="text-xs mt-1" style={{ color: '#346639' }}>✓ {billFile.filename}</div>}
              {!billFile && editing?.bill_filename && <div className="text-xs mt-1" style={{ color: '#5C5C5C' }}>Current: {editing.bill_filename}</div>}
            </div>
            <div className="col-span-2"><Label>Asset Photos (multiple)</Label>
              <input type="file" multiple onChange={onImages} accept="image/*" className="text-sm" data-testid="image-upload" />
              {imageFiles.length > 0 && <div className="text-xs mt-1" style={{ color: '#346639' }}>+ {imageFiles.length} new photo(s)</div>}
              {form.images?.length > 0 && <div className="text-xs mt-1" style={{ color: '#5C5C5C' }}>Existing: {form.images.length}</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="asset-save" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="asset-detail">
          {detail && <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3"><span className="font-mono">{detail.asset_code}</span><StatusPill status={detail.status} /></DialogTitle>
              <DialogDescription>{detail.name}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><b>Category:</b> {detail.category || '—'}</div>
              <div><b>Brand/Model:</b> {detail.brand || ''} {detail.model ? `· ${detail.model}` : ''}</div>
              <div><b>Serial:</b> {detail.serial_no || '—'}</div>
              <div><b>Value:</b> ₹{(detail.value || 0).toLocaleString('en-IN')}</div>
              <div><b>Vendor:</b> {masters.vendors.find(v => v.id === detail.vendor_id)?.name || '—'}</div>
              <div><b>Purchase:</b> {detail.date_of_purchase || '—'}</div>
              <div><b>Warranty:</b> {detail.warranty_start || '—'} → {detail.warranty_end || '—'}</div>
              <div><b>AMC:</b> {detail.amc_start || '—'} → {detail.amc_end || '—'}</div>
              <div><b>Branch:</b> {detail.branch || '—'}</div>
              <div><b>Department:</b> {detail.department || '—'}</div>
              <div><b>Assigned to:</b> {masters.employees.find(e => e.id === detail.assigned_staff_id)?.name || detail.assigned_site || '—'}</div>
              <div><b>Depreciation:</b> {(detail.depreciation_method || 'none').toUpperCase()}</div>
            </div>
            {detail.bill_copy && <div className="mt-3"><a href={detail.bill_copy} download={detail.bill_filename || 'bill'} className="text-xs underline" data-testid="bill-download"><FileText size={12} className="inline mr-1" />Download bill</a></div>}
            {detail.images?.length > 0 && <div className="mt-3"><Label className="text-xs">Photos ({detail.images.length})</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">{detail.images.map((img, i) => <img key={i} src={img.data} alt={img.filename} className="w-full h-20 object-cover rounded border" style={{ borderColor: '#E8E5DF' }} />)}</div>
            </div>}
            <DialogFooter><Button onClick={() => setDetail(null)}>Close</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* QR */}
      <Dialog open={!!qrAsset} onOpenChange={(v) => !v && setQrAsset(null)}>
        <DialogContent data-testid="qr-dialog">
          {qrAsset && <>
            <DialogHeader>
              <DialogTitle>QR Code · {qrAsset.asset_code}</DialogTitle>
              <DialogDescription>Print and stick on the asset. Anyone can scan for a public summary.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-3 py-2">
              <QRCodeSVG value={`${window.location.origin}/scan/asset/${qrAsset.asset_code}`} size={240} />
              <p className="text-xs font-mono">{qrAsset.asset_code}</p>
              <p className="text-sm font-medium text-center">{qrAsset.name}</p>
              <a className="text-xs underline" href={`/scan/asset/${qrAsset.asset_code}`} target="_blank" rel="noreferrer"><ExternalLink size={10} className="inline mr-1" />Public link</a>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => window.print()} data-testid="qr-print"><Printer size={14} className="mr-1" />Print</Button>
              <Button onClick={() => setQrAsset(null)}>Close</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* ASSIGN */}
      <Dialog open={!!assignOpen} onOpenChange={(v) => !v && setAssignOpen(null)}>
        <DialogContent data-testid="assign-dialog">
          {assignOpen && <>
            <DialogHeader>
              <DialogTitle>Assign · {assignOpen.asset_code}</DialogTitle>
              <DialogDescription>Pick a staff member or enter a site/project name.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Assign to staff</Label>
                <Select value={assignForm.staff_id || 'none'} onValueChange={(v) => setAssignForm({ ...assignForm, staff_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="assign-staff"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{masters.employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} · {e.emp_id}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Or Site / Project</Label><Input value={assignForm.site || ''} onChange={(e) => setAssignForm({ ...assignForm, site: e.target.value })} data-testid="assign-site" placeholder="e.g. Banswara Villa Project" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Expected Return</Label><Input type="date" value={assignForm.expected_return || ''} onChange={(e) => setAssignForm({ ...assignForm, expected_return: e.target.value })} /></div>
                <div><Label>Project Tag</Label><Input value={assignForm.project || ''} onChange={(e) => setAssignForm({ ...assignForm, project: e.target.value })} /></div>
              </div>
              <div><Label>Notes</Label><textarea className="w-full border rounded p-2 text-sm" rows={2} value={assignForm.notes || ''} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignOpen(null)}>Cancel</Button>
              <Button onClick={submitAssign} data-testid="assign-submit" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>Assign</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* RETURN */}
      <Dialog open={!!returnOpen} onOpenChange={(v) => !v && setReturnOpen(null)}>
        <DialogContent data-testid="return-dialog">
          {returnOpen && <>
            <DialogHeader>
              <DialogTitle>Return · {returnOpen.asset_code}</DialogTitle>
              <DialogDescription>Capture return condition. Damaged → status=Damaged. Lost → Scrapped.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Condition</Label>
                <Select value={returnForm.condition} onValueChange={(v) => setReturnForm({ ...returnForm, condition: v })}>
                  <SelectTrigger data-testid="return-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good — back to working</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {returnForm.condition === 'damaged' && (
                <div><Label>Damage Penalty (₹)</Label><Input type="number" value={returnForm.damage_cost || 0} onChange={(e) => setReturnForm({ ...returnForm, damage_cost: parseFloat(e.target.value) || 0 })} data-testid="damage-cost" /></div>
              )}
              <div><Label>Notes</Label><textarea className="w-full border rounded p-2 text-sm" rows={2} value={returnForm.notes} onChange={(e) => setReturnForm({ ...returnForm, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReturnOpen(null)}>Cancel</Button>
              <Button onClick={submitReturn} data-testid="return-submit" style={{ backgroundColor: '#C77D38', color: '#FFF' }}>Confirm Return</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* BULK IMPORT */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import Assets (CSV)</DialogTitle>
            <DialogDescription>
              CSV columns (first row): name, category, brand, model, serial_no, value, branch, department, date_of_purchase, warranty_end, amc_end, status
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input type="file" accept=".csv" onChange={onCsvFile} className="text-sm" data-testid="bulk-file" />
            {bulkRows.length > 0 && (
              <>
                <p className="text-xs" style={{ color: '#346639' }}>✓ {bulkRows.length} row(s) parsed</p>
                <div className="max-h-64 overflow-y-auto border rounded" style={{ borderColor: '#E8E5DF' }}>
                  <table className="w-full text-xs">
                    <thead className="sticky top-0" style={{ backgroundColor: '#F7F5F0' }}>
                      <tr><th className="text-left p-1">Name</th><th className="text-left p-1">Brand</th><th className="text-left p-1">Value</th><th className="text-left p-1">Branch</th></tr>
                    </thead>
                    <tbody>{bulkRows.slice(0, 30).map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: '#E8E5DF' }}>
                        <td className="p-1">{r.name}</td><td className="p-1">{r.brand}</td><td className="p-1">{r.value}</td><td className="p-1">{r.branch}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                  {bulkRows.length > 30 && <p className="text-xs text-center py-1" style={{ color: '#5C5C5C' }}>… and {bulkRows.length - 30} more</p>}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={submitBulk} disabled={bulkBusy || bulkRows.length === 0} data-testid="bulk-submit" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>
              {bulkBusy ? 'Importing…' : `Import ${bulkRows.length || 0} Assets`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TRANSFER */}
      <Dialog open={!!xferOpen} onOpenChange={(v) => !v && setXferOpen(null)}>
        <DialogContent data-testid="xfer-dialog">
          {xferOpen && <>
            <DialogHeader>
              <DialogTitle>Transfer · {xferOpen.asset_code}</DialogTitle>
              <DialogDescription>Asset will enter 'in-transit' status until the receiver acknowledges (Transfers tab).</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>To Branch</Label>
                <Select value={xferForm.to_branch || 'none'} onValueChange={(v) => setXferForm({ ...xferForm, to_branch: v === 'none' ? null : v })}>
                  <SelectTrigger data-testid="xfer-branch"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{masters.branches.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>To Store</Label>
                <Select value={xferForm.to_store_id || 'none'} onValueChange={(v) => setXferForm({ ...xferForm, to_store_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{masters.stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>To Staff</Label>
                <Select value={xferForm.to_staff_id || 'none'} onValueChange={(v) => setXferForm({ ...xferForm, to_staff_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{masters.employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} · {e.emp_id}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Input value={xferForm.notes || ''} onChange={(e) => setXferForm({ ...xferForm, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setXferOpen(null)}>Cancel</Button>
              <Button onClick={submitTransfer} data-testid="xfer-submit" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}><Send size={12} className="mr-1" />Initiate Transfer</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* DOCUMENT VAULT */}
      <Dialog open={!!docOpen} onOpenChange={(v) => !v && setDocOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="doc-dialog">
          {docOpen && <>
            <DialogHeader>
              <DialogTitle>Document Vault · {docOpen.asset_code}</DialogTitle>
              <DialogDescription>Invoices, AMC contracts, warranty cards, service reports — all in one place.</DialogDescription>
            </DialogHeader>
            <div className="border rounded p-3 mb-3" style={{ borderColor: '#E8E5DF' }}>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Kind</Label>
                  <Select value={docForm.kind} onValueChange={(v) => setDocForm({ ...docForm, kind: v })}>
                    <SelectTrigger data-testid="doc-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="warranty">Warranty Card</SelectItem>
                      <SelectItem value="amc">AMC Contract</SelectItem>
                      <SelectItem value="service-report">Service Report</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>File</Label><input type="file" onChange={onDocFile} className="text-sm" data-testid="doc-file" /></div>
                <div className="col-span-2"><Label>Note</Label><Input value={docForm.note} onChange={(e) => setDocForm({ ...docForm, note: e.target.value })} /></div>
                <div className="col-span-2 text-right"><Button size="sm" onClick={submitDoc} disabled={!docForm.data} data-testid="doc-add-btn" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>Add Document</Button></div>
              </div>
            </div>
            {docList.length === 0 ? <p className="text-sm text-center py-4" style={{ color: '#5C5C5C' }}>No documents yet.</p> : (
              <ul className="space-y-2">
                {docList.map(d => (
                  <li key={d.id} className="border rounded p-2 flex items-center justify-between" style={{ borderColor: '#E8E5DF' }} data-testid={`doc-${d.id}`}>
                    <div>
                      <div className="text-xs uppercase font-semibold" style={{ color: '#1E3B29' }}>{d.kind}</div>
                      <a href={d.data} download={d.filename} className="text-sm underline">{d.filename}</a>
                      {d.note && <div className="text-xs mt-1" style={{ color: '#5C5C5C' }}>{d.note}</div>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => delDoc(d.id)} style={{ color: '#A43E33' }}><Trash2 size={12} /></Button>
                  </li>
                ))}
              </ul>
            )}
          </>}
        </DialogContent>
      </Dialog>

      {/* DEPRECIATION */}
      <Dialog open={!!deprOpen} onOpenChange={(v) => !v && setDeprOpen(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="depr-dialog">
          {deprOpen && <>
            <DialogHeader>
              <DialogTitle>Depreciation · {deprOpen.asset_code}</DialogTitle>
              <DialogDescription>Book value based on configured method. Set method, expected life, residual value in asset edit.</DialogDescription>
            </DialogHeader>
            {!deprData ? <p className="text-sm" style={{ color: '#5C5C5C' }}>Computing…</p> : (
              <>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="p-3 rounded" style={{ backgroundColor: '#F7F5F0' }}>
                    <div className="text-[10px] uppercase" style={{ color: '#5C5C5C' }}>Method</div>
                    <div className="text-lg font-bold uppercase">{deprData.method}</div>
                  </div>
                  <div className="p-3 rounded" style={{ backgroundColor: '#E8F5E9' }}>
                    <div className="text-[10px] uppercase" style={{ color: '#5C5C5C' }}>Book Value (today)</div>
                    <div className="text-lg font-bold" style={{ color: '#346639' }}>₹{(deprData.book_value || 0).toLocaleString('en-IN')}</div>
                  </div>
                  <div className="p-3 rounded" style={{ backgroundColor: '#F7F5F0' }}>
                    <div className="text-[10px] uppercase" style={{ color: '#5C5C5C' }}>Age</div>
                    <div className="text-lg font-bold">{deprData.years_elapsed}y</div>
                  </div>
                </div>
                {deprData.schedule?.length > 0 ? (
                  <>
                    <h4 className="text-xs uppercase tracking-wider mb-1" style={{ color: '#5C5C5C' }}>Yearly Schedule</h4>
                    <table className="w-full text-sm">
                      <thead style={{ backgroundColor: '#F7F5F0' }}><tr><th className="text-left p-2">Year</th><th className="text-right p-2">Depreciation</th><th className="text-right p-2">Book Value</th></tr></thead>
                      <tbody>{deprData.schedule.map(s => (
                        <tr key={s.year} className="border-t" style={{ borderColor: '#E8E5DF' }}>
                          <td className="p-2">{s.year}</td><td className="p-2 text-right">₹{s.depreciation.toLocaleString('en-IN')}</td><td className="p-2 text-right">₹{s.book_value.toLocaleString('en-IN')}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </>
                ) : <p className="text-sm" style={{ color: '#5C5C5C' }}>No schedule — configure depreciation_method, expected_life_years, residual_value to see annual breakdown.</p>}
              </>
            )}
            <DialogFooter><Button onClick={() => setDeprOpen(null)}>Close</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* AI PREDICT RESULT */}
      <Dialog open={!!aiResult} onOpenChange={(v) => !v && setAiResult(null)}>
        <DialogContent data-testid="ai-result-dialog">
          {aiResult && <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles size={16} style={{ color: '#1565C0' }} />AI Prediction · {aiResult.asset?.asset_code}</DialogTitle>
              <DialogDescription>Based on maintenance history and asset profile.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded text-center" style={{ backgroundColor: '#E3F2FD' }}>
                  <div className="text-[10px] uppercase" style={{ color: '#5C5C5C' }}>Next Due</div>
                  <div className="text-base font-bold" style={{ color: '#1565C0' }}>{aiResult.next_due || '—'}</div>
                </div>
                <div className="p-3 rounded text-center" style={{ backgroundColor: '#F7F5F0' }}>
                  <div className="text-[10px] uppercase" style={{ color: '#5C5C5C' }}>Type</div>
                  <div className="text-base font-bold uppercase">{aiResult.type || '—'}</div>
                </div>
                <div className="p-3 rounded text-center" style={{ backgroundColor: aiResult.risk === 'high' ? '#FEE' : aiResult.risk === 'medium' ? '#FFF8E1' : '#E8F5E9' }}>
                  <div className="text-[10px] uppercase" style={{ color: '#5C5C5C' }}>Risk</div>
                  <div className="text-base font-bold uppercase">{aiResult.risk || '—'}</div>
                </div>
              </div>
              <div className="p-3 rounded border" style={{ borderColor: '#E8E5DF', backgroundColor: '#FFF' }}>
                <div className="text-[10px] uppercase mb-1" style={{ color: '#5C5C5C' }}>AI Reasoning</div>
                <p className="text-sm">{aiResult.reasoning || '—'}</p>
              </div>
            </div>
            <DialogFooter><Button onClick={() => setAiResult(null)}>Close</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================
// 3. Inventory / Consumables
// ============================================================
function InventorySection({ isAdmin, masters }) {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', unit: 'PC', reorder_level: 10 });
  const [txnOpen, setTxnOpen] = useState(null);
  const [txnForm, setTxnForm] = useState({ type: 'in', qty: 1, notes: '', cost: null, site: '' });
  const [txnList, setTxnList] = useState([]);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/assets/consumables`, { withCredentials: true });
    setItems(r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ name: '', unit: 'PC', reorder_level: 10 }); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setDialogOpen(true); };

  const save = async () => {
    try {
      if (editing) await axios.put(`${API}/assets/consumables/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API}/assets/consumables`, form, { withCredentials: true });
      toast.success(editing ? 'Updated' : 'Added'); setDialogOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await axios.delete(`${API}/assets/consumables/${c.id}`, { withCredentials: true });
    load();
  };
  const openTxn = async (c) => {
    setTxnOpen(c); setTxnForm({ type: 'in', qty: 1, notes: '', cost: null, site: '' });
    const r = await axios.get(`${API}/assets/consumables/${c.id}/txns`, { withCredentials: true });
    setTxnList(r.data || []);
  };
  const submitTxn = async () => {
    if (!txnForm.qty || txnForm.qty <= 0) { toast.error('Enter qty > 0'); return; }
    try {
      await axios.post(`${API}/assets/consumables/${txnOpen.id}/txn`, txnForm, { withCredentials: true });
      toast.success('Stock updated');
      const r = await axios.get(`${API}/assets/consumables/${txnOpen.id}/txns`, { withCredentials: true });
      setTxnList(r.data || []); load();
      setTxnForm({ type: 'in', qty: 1, notes: '', cost: null, site: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const aiForecast = async (c) => {
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/assets/consumables/${c.id}/ai-forecast`, {}, { withCredentials: true });
      setAiResult({ item: c, ...r.data });
    } catch (e) { toast.error(e.response?.data?.detail || 'AI forecast failed'); }
    finally { setAiBusy(false); }
  };

  return (
    <>
      <SectionHeader title="Inventory & Consumables" subtitle="Plywood, laminates, hardware, paints, IT consumables — track stock in/out.">
        {isAdmin && <Button onClick={openCreate} data-testid="add-cons-btn" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}><Plus size={14} className="mr-1" />New Item</Button>}
      </SectionHeader>

      <Card>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F7F5F0' }}>
            <tr><th className="text-left p-2">Item</th><th className="text-left p-2">Category</th><th className="text-right p-2">Stock</th><th className="text-right p-2">Reorder Lvl</th><th className="text-left p-2">Unit</th><th className="text-left p-2">Vendor</th><th className="text-right p-2">Actions</th></tr>
          </thead>
          <tbody>
            {items.map(c => {
              const low = (c.stock || 0) <= (c.reorder_level || 0);
              return (
                <tr key={c.id} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`cons-row-${c.id}`}>
                  <td className="p-2 font-medium">{c.name}{low && <span className="ml-2 text-[10px] uppercase px-1 rounded" style={{ backgroundColor: '#FEE', color: '#A43E33' }}>LOW</span>}</td>
                  <td className="p-2 text-xs">{c.category || '—'}</td>
                  <td className="p-2 text-right font-semibold" style={{ color: low ? '#A43E33' : '#346639' }}>{c.stock || 0}</td>
                  <td className="p-2 text-right text-xs">{c.reorder_level}</td>
                  <td className="p-2 text-xs">{c.unit}</td>
                  <td className="p-2 text-xs">{masters.vendors.find(v => v.id === c.vendor_id)?.name || '—'}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openTxn(c)} data-testid={`txn-${c.id}`}>Stock</Button>
                    <Button size="sm" variant="ghost" onClick={() => aiForecast(c)} disabled={aiBusy} data-testid={`ai-fc-${c.id}`} title="AI low-stock forecast" style={{ color: '#1565C0' }}><Sparkles size={12} /></Button>
                    {isAdmin && <Button size="sm" variant="ghost" onClick={() => openEdit(c)}><SettingsIcon size={12} /></Button>}
                    {isAdmin && <Button size="sm" variant="ghost" onClick={() => del(c)} style={{ color: '#A43E33' }}><Trash2 size={12} /></Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.length === 0 && <div className="py-10 text-center text-sm" style={{ color: '#5C5C5C' }}>No consumables yet. Add plywood, hardware, paints to start tracking.</div>}
      </Card>

      {/* CREATE / EDIT */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="cons-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Item' : 'New Consumable'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cons-name" /></div>
            <div><Label>Category</Label><Input value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label>Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONS_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Unit Cost (₹)</Label><Input type="number" value={form.unit_cost || 0} onChange={(e) => setForm({ ...form, unit_cost: parseFloat(e.target.value) || 0 })} /></div>
            <div className="col-span-2"><Label>Vendor</Label>
              <Select value={form.vendor_id || 'none'} onValueChange={(v) => setForm({ ...form, vendor_id: v === 'none' ? null : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{masters.vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button onClick={save} data-testid="cons-save" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>{editing ? 'Update' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TXN */}
      <Dialog open={!!txnOpen} onOpenChange={(v) => !v && setTxnOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="txn-dialog">
          {txnOpen && <>
            <DialogHeader>
              <DialogTitle>{txnOpen.name} — Stock {txnOpen.stock || 0} {txnOpen.unit}</DialogTitle>
              <DialogDescription>Add stock-in / stock-out ledger entries.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2 border rounded p-3 mb-3" style={{ borderColor: '#E8E5DF' }}>
              <div><Label>Type</Label>
                <Select value={txnForm.type} onValueChange={(v) => setTxnForm({ ...txnForm, type: v })}>
                  <SelectTrigger data-testid="txn-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="in">Stock IN</SelectItem><SelectItem value="out">Stock OUT</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Quantity</Label><Input type="number" value={txnForm.qty} onChange={(e) => setTxnForm({ ...txnForm, qty: parseFloat(e.target.value) || 0 })} data-testid="txn-qty" /></div>
              <div><Label>Site / Project</Label><Input value={txnForm.site || ''} onChange={(e) => setTxnForm({ ...txnForm, site: e.target.value })} /></div>
              <div><Label>Cost (optional ₹)</Label><Input type="number" value={txnForm.cost || ''} onChange={(e) => setTxnForm({ ...txnForm, cost: parseFloat(e.target.value) || null })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Input value={txnForm.notes || ''} onChange={(e) => setTxnForm({ ...txnForm, notes: e.target.value })} /></div>
              <div className="col-span-2 text-right"><Button onClick={submitTxn} data-testid="txn-submit" size="sm" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>Record</Button></div>
            </div>
            <h4 className="text-xs uppercase tracking-wider mb-2" style={{ color: '#5C5C5C' }}>Ledger ({txnList.length})</h4>
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: '#F7F5F0' }}><tr><th className="text-left p-2">At</th><th className="text-left p-2">Type</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Bal</th><th className="text-left p-2">Site</th><th className="text-left p-2">By</th></tr></thead>
              <tbody>
                {txnList.map(t => (
                  <tr key={t.id} className="border-t" style={{ borderColor: '#E8E5DF' }}>
                    <td className="p-2 text-xs">{t.at ? new Date(t.at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : ''}</td>
                    <td className="p-2 text-xs uppercase" style={{ color: t.type === 'in' ? '#346639' : '#A43E33' }}>{t.type}</td>
                    <td className="p-2 text-right text-xs">{t.qty}</td>
                    <td className="p-2 text-right font-semibold">{t.balance}</td>
                    <td className="p-2 text-xs">{t.site || '—'}</td>
                    <td className="p-2 text-xs">{t.by_user_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>}
        </DialogContent>
      </Dialog>

      {/* AI FORECAST RESULT (Inventory) */}
      <Dialog open={!!aiResult} onOpenChange={(v) => !v && setAiResult(null)}>
        <DialogContent data-testid="ai-fc-dialog">
          {aiResult && <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles size={16} style={{ color: '#1565C0' }} />Forecast · {aiResult.item?.name}</DialogTitle>
              <DialogDescription>AI estimate based on recent transaction history.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="p-3 rounded text-center" style={{ backgroundColor: '#E3F2FD' }}>
                <div className="text-[10px] uppercase" style={{ color: '#5C5C5C' }}>Daily Burn</div>
                <div className="text-base font-bold" style={{ color: '#1565C0' }}>{aiResult.daily_burn_rate ?? '—'}</div>
              </div>
              <div className="p-3 rounded text-center" style={{ backgroundColor: aiResult.days_until_stockout && aiResult.days_until_stockout < 14 ? '#FEE' : '#F7F5F0' }}>
                <div className="text-[10px] uppercase" style={{ color: '#5C5C5C' }}>Days Left</div>
                <div className="text-base font-bold">{aiResult.days_until_stockout ?? '—'}</div>
              </div>
              <div className="p-3 rounded text-center" style={{ backgroundColor: '#E8F5E9' }}>
                <div className="text-[10px] uppercase" style={{ color: '#5C5C5C' }}>Reorder Qty</div>
                <div className="text-base font-bold" style={{ color: '#346639' }}>{aiResult.recommended_reorder_qty ?? '—'}</div>
              </div>
            </div>
            <div className="p-3 rounded border" style={{ borderColor: '#E8E5DF' }}>
              <div className="text-[10px] uppercase mb-1" style={{ color: '#5C5C5C' }}>AI Reasoning</div>
              <p className="text-sm">{aiResult.reasoning || '—'}</p>
            </div>
            <DialogFooter><Button onClick={() => setAiResult(null)}>Close</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================
// 4. Assignments — history & active
// ============================================================
function AssignmentsSection({ masters }) {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const load = useCallback(async () => {
    const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
    const r = await axios.get(`${API}/assets/assignments${params}`, { withCredentials: true });
    setList(r.data || []);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <SectionHeader title="Asset Assignments" subtitle="Full assignment history across staff & sites.">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger style={{ width: 140 }} data-testid="assign-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="returned">Returned</SelectItem></SelectContent>
        </Select>
      </SectionHeader>
      <Card>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F7F5F0' }}>
            <tr><th className="text-left p-2">Asset</th><th className="text-left p-2">Assigned To</th><th className="text-left p-2">Site / Project</th><th className="text-left p-2">Expected Return</th><th className="text-left p-2">Status</th><th className="text-left p-2">Assigned At</th><th className="text-left p-2">Return Condition</th></tr>
          </thead>
          <tbody>
            {list.map(a => {
              const emp = masters.employees.find(e => e.id === a.staff_id);
              return (
                <tr key={a.id} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`assign-row-${a.id}`}>
                  <td className="p-2 font-mono text-xs">{a.asset_code}</td>
                  <td className="p-2 text-xs">{emp ? `${emp.name} · ${emp.emp_id}` : '—'}</td>
                  <td className="p-2 text-xs">{a.site || '—'}{a.project ? ` · ${a.project}` : ''}</td>
                  <td className="p-2 text-xs">{a.expected_return || '—'}</td>
                  <td className="p-2 text-xs"><span className="px-1.5 py-0.5 rounded text-[10px] uppercase" style={{ backgroundColor: a.status === 'active' ? '#E3F2FD' : '#F5F5F5', color: a.status === 'active' ? '#1565C0' : '#5C5C5C' }}>{a.status}</span></td>
                  <td className="p-2 text-xs">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('en-IN') : ''}</td>
                  <td className="p-2 text-xs">{a.return_condition || '—'}{a.damage_cost ? ` · ₹${a.damage_cost}` : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {list.length === 0 && <div className="py-10 text-center text-sm" style={{ color: '#5C5C5C' }}>No assignments yet. Use the Assets tab to assign one.</div>}
      </Card>
    </>
  );
}

// ============================================================
// 5. Maintenance
// ============================================================
function MaintenanceSection({ isAdmin, masters }) {
  const [assets, setAssets] = useState([]);
  const [list, setList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ type: 'preventive', status: 'scheduled' });
  const load = useCallback(async () => {
    const [a, m] = await Promise.all([
      axios.get(`${API}/assets`, { withCredentials: true }),
      axios.get(`${API}/assets/maintenance`, { withCredentials: true }),
    ]);
    setAssets(a.data || []); setList(m.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ type: 'preventive', status: 'scheduled' }); setDialogOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...m }); setDialogOpen(true); };
  const save = async () => {
    if (!form.asset_id) { toast.error('Pick an asset'); return; }
    try {
      if (editing) await axios.put(`${API}/assets/maintenance/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API}/assets/maintenance`, form, { withCredentials: true });
      toast.success(editing ? 'Updated' : 'Logged'); setDialogOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (m) => {
    if (!window.confirm('Delete maintenance record?')) return;
    await axios.delete(`${API}/assets/maintenance/${m.id}`, { withCredentials: true });
    load();
  };

  return (
    <>
      <SectionHeader title="Maintenance" subtitle="Preventive · Breakdown · AMC tracking with vendor + cost log.">
        {isAdmin && <Button onClick={openCreate} data-testid="add-maint-btn" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}><Plus size={14} className="mr-1" />New Maintenance</Button>}
      </SectionHeader>
      <Card>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F7F5F0' }}>
            <tr><th className="text-left p-2">Asset</th><th className="text-left p-2">Type</th><th className="text-left p-2">Scheduled</th><th className="text-left p-2">Vendor</th><th className="text-right p-2">Cost</th><th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th></tr>
          </thead>
          <tbody>
            {list.map(m => (
              <tr key={m.id} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`maint-row-${m.id}`}>
                <td className="p-2 font-mono text-xs">{m.asset_code}</td>
                <td className="p-2 text-xs uppercase">{m.type}</td>
                <td className="p-2 text-xs">{m.scheduled_at || '—'}</td>
                <td className="p-2 text-xs">{masters.vendors.find(v => v.id === m.vendor_id)?.name || '—'}</td>
                <td className="p-2 text-right text-xs">{m.cost ? `₹${m.cost.toLocaleString('en-IN')}` : '—'}</td>
                <td className="p-2 text-xs uppercase" style={{ color: m.status === 'completed' ? '#346639' : m.status === 'in-progress' ? '#C77D38' : '#1565C0' }}>{m.status}</td>
                <td className="p-2 text-right">
                  {isAdmin && <><Button size="sm" variant="ghost" onClick={() => openEdit(m)}><SettingsIcon size={12} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(m)} style={{ color: '#A43E33' }}><Trash2 size={12} /></Button></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <div className="py-10 text-center text-sm" style={{ color: '#5C5C5C' }}>No maintenance records yet.</div>}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="maint-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Maintenance' : 'New Maintenance'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2"><Label>Asset *</Label>
              <Select value={form.asset_id || 'none'} onValueChange={(v) => setForm({ ...form, asset_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="maint-asset"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{assets.map(a => <SelectItem key={a.id} value={a.id}>{a.asset_code} · {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MAINT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MAINT_STATUSES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Scheduled</Label><Input type="date" value={form.scheduled_at || ''} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></div>
            <div><Label>Completed</Label><Input type="date" value={form.completed_at || ''} onChange={(e) => setForm({ ...form, completed_at: e.target.value })} /></div>
            <div><Label>Vendor</Label>
              <Select value={form.vendor_id || 'none'} onValueChange={(v) => setForm({ ...form, vendor_id: v === 'none' ? null : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{masters.vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Cost (₹)</Label><Input type="number" value={form.cost || 0} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })} /></div>
            <div className="col-span-2"><Label>Notes</Label><textarea className="w-full border rounded p-2 text-sm" rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button onClick={save} data-testid="maint-save" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>{editing ? 'Update' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================
// 6. Audits — create, scan, report
// ============================================================
function AuditsSection({ isAdmin, masters }) {
  const [audits, setAudits] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: '', scheduled_at: '', branch: '', notes: '' });
  const [scanOpen, setScanOpen] = useState(null);
  const [scanForm, setScanForm] = useState({ asset_code: '', condition: 'ok', notes: '' });
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/assets/audits`, { withCredentials: true });
    setAudits(r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    try {
      await axios.post(`${API}/assets/audits`, form, { withCredentials: true });
      toast.success('Audit created'); setNewOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const openScan = async (a) => {
    setScanOpen(a); setScanForm({ asset_code: '', condition: 'ok', notes: '' });
    const r = await axios.get(`${API}/assets/audits/${a.id}/report`, { withCredentials: true });
    setReport(r.data);
  };
  const submitScan = async () => {
    if (!scanForm.asset_code) { toast.error('Enter / scan asset code'); return; }
    try {
      await axios.post(`${API}/assets/audits/${scanOpen.id}/scan`, scanForm, { withCredentials: true });
      toast.success('Scan recorded'); setScanForm({ asset_code: '', condition: 'ok', notes: '' });
      const r = await axios.get(`${API}/assets/audits/${scanOpen.id}/report`, { withCredentials: true });
      setReport(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const closeAudit = async (a) => {
    if (!window.confirm(`Close audit ${a.name}?`)) return;
    await axios.put(`${API}/assets/audits/${a.id}/close`, {}, { withCredentials: true });
    toast.success('Audit closed'); setScanOpen(null); load();
  };

  return (
    <>
      <SectionHeader title="Audits & Verification" subtitle="Physical asset verification by QR scan + mismatch reports.">
        {isAdmin && <Button onClick={() => setNewOpen(true)} data-testid="new-audit-btn" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}><Plus size={14} className="mr-1" />New Audit</Button>}
      </SectionHeader>
      <Card>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F7F5F0' }}>
            <tr><th className="text-left p-2">Name</th><th className="text-left p-2">Scheduled</th><th className="text-left p-2">Branch</th><th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th></tr>
          </thead>
          <tbody>
            {audits.map(a => (
              <tr key={a.id} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`audit-row-${a.id}`}>
                <td className="p-2 font-medium">{a.name}</td>
                <td className="p-2 text-xs">{a.scheduled_at || '—'}</td>
                <td className="p-2 text-xs">{a.branch || '—'}</td>
                <td className="p-2 text-xs uppercase" style={{ color: a.status === 'open' ? '#1565C0' : '#5C5C5C' }}>{a.status}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openScan(a)} data-testid={`open-audit-${a.id}`}>Open</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {audits.length === 0 && <div className="py-10 text-center text-sm" style={{ color: '#5C5C5C' }}>No audits yet.</div>}
      </Card>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Audit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="audit-name" placeholder="e.g. Q1 Surprise Audit – HO" /></div>
            <div><Label>Scheduled</Label><Input type="date" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></div>
            <div><Label>Branch (optional scope)</Label>
              <Select value={form.branch || 'none'} onValueChange={(v) => setForm({ ...form, branch: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent><SelectItem value="none">All branches</SelectItem>{masters.branches.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button><Button onClick={create} data-testid="audit-create" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!scanOpen} onOpenChange={(v) => !v && setScanOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="scan-dialog">
          {scanOpen && report && <>
            <DialogHeader>
              <DialogTitle>Audit · {scanOpen.name}</DialogTitle>
              <DialogDescription>Scan or enter asset codes. We track verified / damaged / not-found / missing automatically.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-2 mb-3 text-center">
              <div className="p-2 rounded" style={{ backgroundColor: '#E8F5E9' }}><div className="text-xs uppercase" style={{ color: '#346639' }}>Verified</div><div className="text-xl font-bold" style={{ color: '#346639' }}>{report.totals.verified}</div></div>
              <div className="p-2 rounded" style={{ backgroundColor: '#FEE' }}><div className="text-xs uppercase" style={{ color: '#A43E33' }}>Damaged</div><div className="text-xl font-bold" style={{ color: '#A43E33' }}>{report.totals.damaged}</div></div>
              <div className="p-2 rounded" style={{ backgroundColor: '#FFF8E1' }}><div className="text-xs uppercase" style={{ color: '#C77D38' }}>Not Found</div><div className="text-xl font-bold" style={{ color: '#C77D38' }}>{report.totals.not_found}</div></div>
              <div className="p-2 rounded" style={{ backgroundColor: '#F5F5F5' }}><div className="text-xs uppercase" style={{ color: '#5C5C5C' }}>Missing</div><div className="text-xl font-bold" style={{ color: '#5C5C5C' }}>{report.totals.missing}</div></div>
            </div>
            {scanOpen.status === 'open' && (
              <div className="border rounded p-3 mb-3" style={{ borderColor: '#E8E5DF' }}>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2"><Label>Asset Code</Label><Input value={scanForm.asset_code} onChange={(e) => setScanForm({ ...scanForm, asset_code: e.target.value })} data-testid="scan-code" placeholder="AST-2026-0001" /></div>
                  <div><Label>Condition</Label>
                    <Select value={scanForm.condition} onValueChange={(v) => setScanForm({ ...scanForm, condition: v })}>
                      <SelectTrigger data-testid="scan-condition"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="ok">OK</SelectItem><SelectItem value="damaged">Damaged</SelectItem><SelectItem value="missing">Missing</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Notes</Label><Input value={scanForm.notes} onChange={(e) => setScanForm({ ...scanForm, notes: e.target.value })} /></div>
                  <div className="flex items-end"><Button onClick={submitScan} className="w-full" data-testid="scan-submit" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>Record</Button></div>
                </div>
              </div>
            )}

            <h4 className="text-xs uppercase tracking-wider mb-1" style={{ color: '#5C5C5C' }}>Recent scans</h4>
            <table className="w-full text-sm mb-3">
              <thead style={{ backgroundColor: '#F7F5F0' }}><tr><th className="text-left p-2">Code</th><th className="text-left p-2">Name</th><th className="text-left p-2">Condition</th><th className="text-left p-2">By</th><th className="text-left p-2">At</th></tr></thead>
              <tbody>{report.scans.map(s => (
                <tr key={s.id} className="border-t" style={{ borderColor: '#E8E5DF' }}>
                  <td className="p-2 font-mono text-xs">{s.asset_code}</td><td className="p-2 text-xs">{s.asset_name || (s.found ? '—' : <span style={{ color: '#A43E33' }}>Not in register!</span>)}</td>
                  <td className="p-2 text-xs uppercase">{s.condition}</td><td className="p-2 text-xs">{s.by_user_name}</td>
                  <td className="p-2 text-xs">{s.at ? new Date(s.at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : ''}</td>
                </tr>
              ))}</tbody>
            </table>

            {report.missing_assets?.length > 0 && (
              <>
                <h4 className="text-xs uppercase tracking-wider mb-1" style={{ color: '#A43E33' }}>Missing from this audit ({report.missing_assets.length})</h4>
                <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                  {report.missing_assets.map(a => <li key={a.id} className="font-mono">{a.asset_code} — {a.name}</li>)}
                </ul>
              </>
            )}

            <DialogFooter>
              {isAdmin && scanOpen.status === 'open' && <Button variant="outline" onClick={() => closeAudit(scanOpen)} data-testid="close-audit" style={{ color: '#A43E33' }}>Close Audit</Button>}
              <Button onClick={() => setScanOpen(null)}>Done</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================
// 7. Approvals (asset-scoped)
// ============================================================
function ApprovalsSection() {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/assets/approval-requests?status=${statusFilter}`, { withCredentials: true });
    setList(r.data || []);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      <SectionHeader title="Asset Approvals" subtitle="Pending requests for transfer, scrap, replacement, purchase, maintenance.">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger style={{ width: 130 }}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
        </Select>
      </SectionHeader>
      <Card>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F7F5F0' }}>
            <tr><th className="text-left p-2">When</th><th className="text-left p-2">Type</th><th className="text-left p-2">Requested By</th><th className="text-left p-2">Description</th><th className="text-left p-2">Status</th></tr>
          </thead>
          <tbody>
            {list.map(r => (
              <tr key={r.id} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`apr-row-${r.id}`}>
                <td className="p-2 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : ''}</td>
                <td className="p-2 text-xs uppercase">{(r.action_type || '').replace('asset_', '')}</td>
                <td className="p-2 text-xs">{r.requested_by_name}</td>
                <td className="p-2 text-xs">{r.description || '—'}</td>
                <td className="p-2 text-xs uppercase" style={{ color: r.status === 'pending' ? '#C77D38' : r.status === 'approved' ? '#346639' : '#A43E33' }}>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <div className="py-10 text-center text-sm" style={{ color: '#5C5C5C' }}>No requests in this state. Approve / reject from the global Admin → Approvals panel.</div>}
      </Card>
    </>
  );
}

// ============================================================
// 8. Vendors
// ============================================================
function VendorsSection({ isAdmin, masters }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'supply' });
  const save = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      if (editing) await axios.put(`${API}/assets/vendors/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API}/assets/vendors`, form, { withCredentials: true });
      toast.success(editing ? 'Updated' : 'Added'); setOpen(false); masters.reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (v) => {
    if (!window.confirm(`Delete ${v.name}?`)) return;
    await axios.delete(`${API}/assets/vendors/${v.id}`, { withCredentials: true });
    masters.reload();
  };
  return (
    <>
      <SectionHeader title="Vendors" subtitle="Suppliers + service partners for assets, AMC, repairs, consumables.">
        {isAdmin && <Button onClick={() => { setEditing(null); setForm({ name: '', category: 'supply' }); setOpen(true); }} data-testid="add-vendor-btn" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}><Plus size={14} className="mr-1" />New Vendor</Button>}
      </SectionHeader>
      <Card>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F7F5F0' }}>
            <tr><th className="text-left p-2">Name</th><th className="text-left p-2">Contact</th><th className="text-left p-2">Phone</th><th className="text-left p-2">Email</th><th className="text-left p-2">Category</th><th className="text-right p-2">Actions</th></tr>
          </thead>
          <tbody>
            {masters.vendors.map(v => (
              <tr key={v.id} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`vendor-row-${v.id}`}>
                <td className="p-2 font-medium">{v.name}</td>
                <td className="p-2 text-xs">{v.contact_person || '—'}</td>
                <td className="p-2 text-xs">{v.phone || '—'}</td>
                <td className="p-2 text-xs">{v.email || '—'}</td>
                <td className="p-2 text-xs uppercase">{v.category || '—'}</td>
                <td className="p-2 text-right">
                  {isAdmin && <><Button size="sm" variant="ghost" onClick={() => { setEditing(v); setForm(v); setOpen(true); }}><SettingsIcon size={12} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(v)} style={{ color: '#A43E33' }}><Trash2 size={12} /></Button></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {masters.vendors.length === 0 && <div className="py-10 text-center text-sm" style={{ color: '#5C5C5C' }}>No vendors yet.</div>}
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="vendor-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Vendor' : 'New Vendor'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" /></div>
            <div><Label>Contact Person</Label><Input value={form.contact_person || ''} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label>Email</Label><Input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin || ''} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
            <div><Label>Category</Label>
              <Select value={form.category || 'supply'} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="supply">Supply</SelectItem><SelectItem value="service">Service</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="vendor-save" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>{editing ? 'Update' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================
// 9. Employees (read-only proxy of pe_staff)
// ============================================================
function EmployeesSection({ employees }) {
  return (
    <>
      <SectionHeader title="Employees" subtitle="Read-only view from People app. Manage staff under /people." />
      <Card>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F7F5F0' }}><tr><th className="text-left p-2">Emp ID</th><th className="text-left p-2">Name</th><th className="text-left p-2">Designation</th><th className="text-left p-2">Department</th><th className="text-left p-2">Branch</th></tr></thead>
          <tbody>{employees.map(e => (
            <tr key={e.id} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`emp-row-${e.id}`}>
              <td className="p-2 font-mono text-xs">{e.emp_id}</td><td className="p-2 font-medium">{e.name}</td>
              <td className="p-2 text-xs">{e.designation || '—'}</td><td className="p-2 text-xs">{e.department || '—'}</td><td className="p-2 text-xs">{e.branch || '—'}</td>
            </tr>
          ))}</tbody>
        </table>
        {employees.length === 0 && <div className="py-10 text-center text-sm" style={{ color: '#5C5C5C' }}>No staff records yet. Add them in the People app.</div>}
      </Card>
    </>
  );
}

// ============================================================
// 10. Settings (Categories, Stores)
// ============================================================
function SettingsSection({ masters }) {
  const [catName, setCatName] = useState('');
  const [catKind, setCatKind] = useState('hard');
  const [storeForm, setStoreForm] = useState({ name: '', location: '', space_type: 'showroom' });

  const addCat = async () => {
    if (!catName) return;
    try { await axios.post(`${API}/assets/categories`, { name: catName, kind: catKind }, { withCredentials: true }); setCatName(''); masters.reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const delCat = async (id) => { await axios.delete(`${API}/assets/categories/${id}`, { withCredentials: true }); masters.reload(); };

  const addStore = async () => {
    if (!storeForm.name) return;
    try { await axios.post(`${API}/assets/stores`, storeForm, { withCredentials: true }); setStoreForm({ name: '', location: '', space_type: 'showroom' }); masters.reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const delStore = async (id) => { await axios.delete(`${API}/assets/stores/${id}`, { withCredentials: true }); masters.reload(); };

  return (
    <>
      <SectionHeader title="Settings" subtitle="Asset categories + stores/spaces. Branches & departments are shared with Tickets." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <h3 className="font-medium mb-3 flex items-center gap-2"><Boxes size={16} />Asset Categories</h3>
          <div className="flex gap-2 mb-3">
            <Input placeholder="Category name" value={catName} onChange={(e) => setCatName(e.target.value)} data-testid="cat-name" />
            <Select value={catKind} onValueChange={setCatKind}>
              <SelectTrigger style={{ width: 130 }}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="hard">Hard</SelectItem><SelectItem value="soft">Soft</SelectItem><SelectItem value="consumable">Consumable</SelectItem></SelectContent>
            </Select>
            <Button onClick={addCat} data-testid="cat-add" size="sm" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}><Plus size={14} /></Button>
          </div>
          <ul className="space-y-1 text-sm">
            {masters.categories.map(c => (
              <li key={c.id} className="flex justify-between border-b py-1" style={{ borderColor: '#E8E5DF' }}>
                <span>{c.name} <span className="text-xs" style={{ color: '#5C5C5C' }}>· {c.kind}</span></span>
                <Button size="sm" variant="ghost" onClick={() => delCat(c.id)} style={{ color: '#A43E33' }}><Trash2 size={12} /></Button>
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <h3 className="font-medium mb-3 flex items-center gap-2"><StoreIcon size={16} />Stores / Spaces</h3>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Input placeholder="Store name" value={storeForm.name} onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })} data-testid="store-name" />
            <Input placeholder="Location" value={storeForm.location} onChange={(e) => setStoreForm({ ...storeForm, location: e.target.value })} />
            <Select value={storeForm.space_type} onValueChange={(v) => setStoreForm({ ...storeForm, space_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="showroom">Showroom</SelectItem><SelectItem value="digital">Digital</SelectItem></SelectContent>
            </Select>
            <Button onClick={addStore} data-testid="store-add" size="sm" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}><Plus size={14} className="mr-1" />Add</Button>
          </div>
          <ul className="space-y-1 text-sm">
            {masters.stores.map(s => (
              <li key={s.id} className="flex justify-between border-b py-1" style={{ borderColor: '#E8E5DF' }}>
                <span>{s.name} <span className="text-xs" style={{ color: '#5C5C5C' }}>· {s.location} · {s.space_type}</span></span>
                <Button size="sm" variant="ghost" onClick={() => delStore(s.id)} style={{ color: '#A43E33' }}><Trash2 size={12} /></Button>
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <h3 className="font-medium mb-3 flex items-center gap-2"><Building2 size={16} />Branches <span className="text-xs ml-2" style={{ color: '#5C5C5C' }}>(shared with Tickets)</span></h3>
          <ul className="text-sm space-y-1">{masters.branches.map(b => <li key={b.id} className="border-b py-1" style={{ borderColor: '#E8E5DF' }}>{b.name} <span className="text-xs" style={{ color: '#5C5C5C' }}>· {b.location}</span></li>)}</ul>
          {masters.branches.length === 0 && <p className="text-xs" style={{ color: '#5C5C5C' }}>Add branches in the Tickets module.</p>}
        </Card>

        <Card>
          <h3 className="font-medium mb-3 flex items-center gap-2"><UsersIcon size={16} />Departments <span className="text-xs ml-2" style={{ color: '#5C5C5C' }}>(shared with Tickets)</span></h3>
          <ul className="text-sm space-y-1">{masters.departments.map(d => <li key={d.id} className="border-b py-1" style={{ borderColor: '#E8E5DF' }}>{d.name}</li>)}</ul>
          {masters.departments.length === 0 && <p className="text-xs" style={{ color: '#5C5C5C' }}>Add departments in the Tickets module.</p>}
        </Card>
      </div>
    </>
  );
}


// ============================================================
// Transfers section (i)
// ============================================================
function TransfersSection({ isAdmin }) {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('in-transit');
  const load = useCallback(async () => {
    const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
    const r = await axios.get(`${API}/assets/transfers${params}`, { withCredentials: true });
    setList(r.data || []);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const acknowledge = async (t) => {
    if (!window.confirm(`Acknowledge receipt of ${t.asset_code}?`)) return;
    try { await axios.post(`${API}/assets/transfers/${t.id}/acknowledge`, {}, { withCredentials: true });
      toast.success('Transfer acknowledged · asset moved to destination'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const cancel = async (t) => {
    if (!window.confirm(`Cancel transfer of ${t.asset_code}?`)) return;
    try { await axios.post(`${API}/assets/transfers/${t.id}/cancel`, {}, { withCredentials: true });
      toast.success('Transfer cancelled'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <>
      <SectionHeader title="Asset Transfers" subtitle="Branch-to-branch / staff-to-staff transfers with acknowledgment workflow.">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger style={{ width: 140 }} data-testid="xfer-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="in-transit">In Transit</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </SectionHeader>
      <Card>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F7F5F0' }}>
            <tr><th className="text-left p-2">Asset</th><th className="text-left p-2">From</th><th className="text-left p-2">To</th><th className="text-left p-2">Initiated</th><th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th></tr>
          </thead>
          <tbody>
            {list.map(t => (
              <tr key={t.id} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`xfer-row-${t.id}`}>
                <td className="p-2 font-mono text-xs">{t.asset_code}</td>
                <td className="p-2 text-xs">{t.from_branch || '—'}</td>
                <td className="p-2 text-xs">{t.to_branch || t.to_store_id || t.to_staff_id || '—'}</td>
                <td className="p-2 text-xs">{t.initiated_at ? new Date(t.initiated_at).toLocaleDateString('en-IN') : ''} · {t.initiated_by_name}</td>
                <td className="p-2 text-xs uppercase" style={{ color: t.status === 'in-transit' ? '#C77D38' : t.status === 'received' ? '#346639' : '#5C5C5C' }}>{t.status}</td>
                <td className="p-2 text-right">
                  {t.status === 'in-transit' && <Button size="sm" variant="outline" onClick={() => acknowledge(t)} data-testid={`xfer-ack-${t.id}`} style={{ color: '#346639', borderColor: '#346639' }}>Acknowledge</Button>}
                  {isAdmin && t.status === 'in-transit' && <Button size="sm" variant="ghost" onClick={() => cancel(t)} style={{ color: '#A43E33' }} data-testid={`xfer-cancel-${t.id}`}>Cancel</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <div className="py-10 text-center text-sm" style={{ color: '#5C5C5C' }}>No transfers in this state. Initiate one from any asset's row.</div>}
      </Card>
    </>
  );
}

// ============================================================
// Reports section (c)
// ============================================================
function ReportsSection() {
  const [byBranch, setByBranch] = useState([]);
  const [byEmp, setByEmp] = useState([]);
  const [util, setUtil] = useState(null);
  const [aging, setAging] = useState(null);
  const [activeReport, setActiveReport] = useState('branch');

  useEffect(() => {
    (async () => {
      const [b, e, u, a] = await Promise.all([
        axios.get(`${API}/assets/reports/by-branch`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/assets/reports/by-employee`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/assets/reports/utilization`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/assets/reports/aging`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setByBranch(b.data || []); setByEmp(e.data || []); setUtil(u.data); setAging(a.data);
    })();
  }, []);

  return (
    <>
      <SectionHeader title="Reports & Analytics" subtitle="Branch-wise · Employee-wise · Utilization · Aging analysis.">
        <Button variant="outline" size="sm" onClick={() => { window.location.href = `${API}/assets/export/excel`; }} data-testid="reports-excel"><Download size={14} className="mr-1" />Asset Register Excel</Button>
      </SectionHeader>

      <div className="flex gap-2 mb-3">
        {[
          { k: 'branch', label: 'By Branch' },
          { k: 'employee', label: 'By Employee' },
          { k: 'util', label: 'Utilization' },
          { k: 'aging', label: 'Aging' },
        ].map(t => (
          <Button key={t.k} variant={activeReport === t.k ? 'default' : 'outline'} size="sm" onClick={() => setActiveReport(t.k)} data-testid={`report-tab-${t.k}`}
            style={activeReport === t.k ? { backgroundColor: '#1E3B29', color: '#FFF' } : {}}>{t.label}</Button>
        ))}
      </div>

      {activeReport === 'branch' && (
        <Card>
          <h3 className="font-medium mb-3 text-sm">Branch-wise Distribution</h3>
          <table className="w-full text-sm">
            <thead style={{ backgroundColor: '#F7F5F0' }}>
              <tr><th className="text-left p-2">Branch</th><th className="text-right p-2">Count</th><th className="text-right p-2">Value (₹)</th><th className="text-right p-2">Working</th><th className="text-right p-2">Service</th><th className="text-right p-2">Damaged</th><th className="text-right p-2">Scrapped</th></tr>
            </thead>
            <tbody>
              {byBranch.map(b => (
                <tr key={b.branch} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`report-branch-${b.branch}`}>
                  <td className="p-2 font-medium">{b.branch}</td>
                  <td className="p-2 text-right">{b.count}</td>
                  <td className="p-2 text-right">{b.value.toLocaleString('en-IN')}</td>
                  <td className="p-2 text-right" style={{ color: '#346639' }}>{b.working}</td>
                  <td className="p-2 text-right" style={{ color: '#C77D38' }}>{b.under_service}</td>
                  <td className="p-2 text-right" style={{ color: '#A43E33' }}>{b.damaged}</td>
                  <td className="p-2 text-right" style={{ color: '#5C5C5C' }}>{b.scrapped}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {byBranch.length === 0 && <div className="py-8 text-center text-sm" style={{ color: '#5C5C5C' }}>No data yet.</div>}
        </Card>
      )}

      {activeReport === 'employee' && (
        <Card>
          <h3 className="font-medium mb-3 text-sm">Employee-wise Assets</h3>
          <table className="w-full text-sm">
            <thead style={{ backgroundColor: '#F7F5F0' }}>
              <tr><th className="text-left p-2">Employee</th><th className="text-left p-2">Emp ID</th><th className="text-right p-2">Asset Count</th><th className="text-right p-2">Total Value (₹)</th><th className="text-left p-2">Assets</th></tr>
            </thead>
            <tbody>
              {byEmp.map(e => (
                <tr key={e.staff_id} className="border-t" style={{ borderColor: '#E8E5DF' }} data-testid={`report-emp-${e.staff_id}`}>
                  <td className="p-2 font-medium">{e.name}</td>
                  <td className="p-2 text-xs">{e.emp_id}</td>
                  <td className="p-2 text-right">{e.count}</td>
                  <td className="p-2 text-right">{e.value.toLocaleString('en-IN')}</td>
                  <td className="p-2 text-xs">{e.assets.slice(0, 3).map(a => a.asset_code).join(', ')}{e.assets.length > 3 ? ` +${e.assets.length - 3}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {byEmp.length === 0 && <div className="py-8 text-center text-sm" style={{ color: '#5C5C5C' }}>No assets assigned to employees yet.</div>}
        </Card>
      )}

      {activeReport === 'util' && util && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card><div className="text-xs uppercase mb-1" style={{ color: '#5C5C5C' }}>Total Assets</div><div className="text-3xl font-bold">{util.total}</div></Card>
          <Card><div className="text-xs uppercase mb-1" style={{ color: '#5C5C5C' }}>Currently Assigned</div><div className="text-3xl font-bold" style={{ color: '#1E3B29' }}>{util.assigned}</div><div className="text-xs mt-1" style={{ color: '#5C5C5C' }}>{util.utilization_pct}% of total</div></Card>
          <Card><div className="text-xs uppercase mb-1" style={{ color: '#5C5C5C' }}>In Working Service</div><div className="text-3xl font-bold" style={{ color: '#346639' }}>{util.in_service}</div><div className="text-xs mt-1" style={{ color: '#5C5C5C' }}>{util.service_pct}% operational</div></Card>
          <Card><div className="text-xs uppercase mb-1" style={{ color: '#5C5C5C' }}>Idle / Unutilized</div><div className="text-3xl font-bold" style={{ color: '#C77D38' }}>{util.total - util.assigned}</div></Card>
        </div>
      )}

      {activeReport === 'aging' && aging && (
        <Card>
          <h3 className="font-medium mb-3 text-sm">Asset Age Distribution</h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {Object.entries(aging).map(([bucket, count]) => (
              <div key={bucket} className="p-3 rounded text-center" style={{ backgroundColor: '#F7F5F0' }} data-testid={`aging-${bucket}`}>
                <div className="text-xs uppercase font-medium" style={{ color: '#5C5C5C' }}>{bucket}</div>
                <div className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>{count}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

